import React from "react";
import { FolderOpen, Save, Trash2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { routeDistance } from "./navigationRebuildUtils";

export default function SavedRoutePlansPanel({ routePoints, onLoadRoute }) {
  const [routes, setRoutes] = React.useState([]);
  const [name, setName] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const loadRoutes = React.useCallback(async () => {
    const records = await base44.entities.Route.list("-updated_date", 20);
    setRoutes(records);
  }, []);

  React.useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const saveRoute = async () => {
    if (!routePoints.length || !name.trim()) return;
    setSaving(true);
    const distance = routeDistance(routePoints);
    await base44.entities.Route.create({
      name: name.trim(),
      waypoints: JSON.stringify(routePoints),
      distance_nm: Number(distance.toFixed(2)),
      estimated_duration_hours: Number((distance / 6).toFixed(2)),
      average_speed_knots: 6,
      last_used: new Date().toISOString(),
    });
    setName("");
    setSaving(false);
    await loadRoutes();
  };

  const reloadRoute = async (route) => {
    const points = JSON.parse(route.waypoints || "[]");
    onLoadRoute(points);
    await base44.entities.Route.update(route.id, { last_used: new Date().toISOString() });
    await loadRoutes();
  };

  const deleteRoute = async (routeId) => {
    await base44.entities.Route.delete(routeId);
    await loadRoutes();
  };

  return (
    <div className="mt-4 border-t border-slate-200 pt-3 dark:border-white/10">
      <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-primary">
        <Save className="h-3.5 w-3.5" /> Saved routes
      </div>

      <div className="flex gap-2">
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Trip route name"
          className="h-10 min-h-10 rounded-xl text-sm"
        />
        <Button type="button" size="sm" onClick={saveRoute} disabled={!routePoints.length || !name.trim() || saving}>
          Save
        </Button>
      </div>

      <div className="mt-3 max-h-44 space-y-2 overflow-y-auto pr-1">
        {routes.length === 0 ? (
          <p className="rounded-2xl bg-slate-100 p-3 text-xs font-semibold text-slate-600 dark:bg-white/5 dark:text-slate-300">No saved routes yet.</p>
        ) : routes.map((route) => (
          <div
            key={route.id}
            className="flex w-full items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50/90 p-2 transition hover:border-primary/50 dark:border-white/10 dark:bg-white/5"
          >
            <button
              type="button"
              onClick={() => reloadRoute(route)}
              className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
            >
              <span className="min-w-0">
                <span className="block truncate text-xs font-black text-slate-900 dark:text-white">{route.name}</span>
                <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400">{Number(route.distance_nm || 0).toFixed(2)} NM</span>
              </span>
              <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
            </button>
            <button
              type="button"
              onClick={() => deleteRoute(route.id)}
              aria-label={`Delete saved route ${route.name}`}
              title="Delete route"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}